import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import Stats from "../Product/stats";
import "./total_invoice.css";
import {
  Plus,
  Search,
  Filter,
  Trash2,
  Download,
  Printer,
  RefreshCcw,
  Pencil,
  CheckCircle2,
} from "lucide-react";

export default function TotalInvoices() {
  const { companyId } = useCompany();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState([
    { amount: "₦0.00", description: "Total Invoices" },
    { amount: 0, description: "Paid Invoices" },
    { amount: "₦0.00", description: "Total Value of Paid Invoices" },
  ]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8;

  // Fetch all invoices (UNCHANGED logic)
  const fetchInvoices = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase
      .from("invoices")
      .select("*")
      .eq("company_id", companyId);

    if (error) {
      console.error("Error fetching invoices:", error);
    } else {
      setInvoices(data || []);
      setFiltered(data || []);
      computeStats(data || []);
      setCurrentPage(1);
    }
  }, [companyId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Compute stats (kept semantics)
  function computeStats(list) {
    const totalAmount = list.reduce((sum, inv) => sum + (inv.total || 0), 0);
    const paidList = list.filter(
      (inv) => (inv.status || "").toLowerCase() === "paid"
    );
    const paidCount = paidList.length;
    const paidTotal = paidList.reduce((sum, inv) => sum + (inv.total || 0), 0);

    setStats([
      {
        amount: `₦${totalAmount.toLocaleString()}`,
        description: "Total Invoices",
      },
      { amount: paidCount, description: "Paid Invoices" },
      {
        amount: `₦${paidTotal.toLocaleString()}`,
        description: "Total Value of Paid Invoices",
      },
    ]);
  }

  // Search + status filter (client-only)
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    const next = invoices.filter((inv) => {
      const matchesText = (inv.customer || "").toLowerCase().includes(q);
      const s = (inv.status || "unknown").toLowerCase();
      const matchesStatus = statusFilter === "all" ? true : s === statusFilter;
      return matchesText && matchesStatus;
    });
    setFiltered(next);
    setCurrentPage(1);
  }, [searchQuery, statusFilter, invoices]);

  // Delete (scoped to company)
  const handleDelete = async (id) => {
    const { error } = await supabase
      .from("invoices")
      .delete()
      .eq("id", id)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error deleting invoice:", error);
    } else {
      const next = filtered.filter((inv) => inv.id !== id);
      setFiltered(next);
      const newAll = invoices.filter((inv) => inv.id !== id);
      setInvoices(newAll);
      computeStats(newAll);
    }
  };

  // Mark as paid (UNCHANGED semantics)
  const handleReceivePayment = async (id) => {
    const { error } = await supabase
      .from("invoices")
      .update({ status: "paid" })
      .eq("id", id)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error updating status:", error);
      alert("Failed to update payment status.");
    } else {
      alert("Marked as paid.");
      fetchInvoices();
    }
  };

  // Navigation (UNCHANGED)
  const handleEdit = (id) => navigate(`/invoice/${id}`);
  const handleAdd = () => navigate("/invoice");

  // UI helpers
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSlice = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  const refresh = () => fetchInvoices();
  const printList = () => window.print();

  const exportCSV = () => {
    const csvRows = [
      ["Customer", "Invoice #", "Date", "Due", "Status", "Total (NGN)"].join(
        ","
      ),
      ...filtered.map((inv) =>
        [
          (inv.customer || "").replaceAll(",", " "),
          inv.invoiceNumber || "",
          inv.date || "",
          inv.expiryDate || "",
          inv.status || "",
          (inv.total || 0).toString(),
        ].join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kostpadi-invoices.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const badgeClass = (status) => {
    const s = (status || "unknown").toLowerCase();
    if (s === "paid") return "badge paid";
    if (s === "open" || s === "pending") return "badge open";
    if (s === "overdue") return "badge overdue";
    return "badge unknown";
  };

  return (
    <div className="invoices-page">
      {/* HERO */}
      <header className="invoices-hero">
        <div>
          <p className="invoices-eyebrow">Sales & Receivables</p>

          <h1>Total Invoices</h1>

          <p>
            Search, filter, export and manage all customer invoices, payment
            status and receivable follow-ups.
          </p>
        </div>

        <div className="invoices-hero-actions">
          <button className="invoice-btn soft" onClick={refresh}>
            <RefreshCcw size={16} />
            Refresh
          </button>

          <button className="invoice-btn soft" onClick={printList}>
            <Printer size={16} />
            Print
          </button>

          <button className="invoice-btn soft" onClick={exportCSV}>
            <Download size={16} />
            Export
          </button>

          <button className="invoice-btn primary" onClick={handleAdd}>
            <Plus size={16} />
            New Invoice
          </button>
        </div>
      </header>

      {/* STATS */}
      <section className="invoice-stats-panel">
        <Stats stats={stats} />
      </section>

      {/* FILTERS */}
      <section className="invoice-panel">
        <div className="invoice-panel-head">
          <div>
            <h3>Search & Filters</h3>
            <p>Find invoices by customer name, status or payment condition.</p>
          </div>
        </div>

        <div className="invoice-filters">
          <div className="invoice-search">
            <Search size={16} />

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by customer..."
            />
          </div>

          <div className="invoice-tabs">
            <span className="invoice-tabs-label">
              <Filter size={14} />
              Status
            </span>

            {["all", "open", "paid", "overdue"].map((s) => (
              <button
                key={s}
                className={`invoice-tab ${statusFilter === s ? "active" : ""}`}
                onClick={() => setStatusFilter(s)}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className="invoice-panel">
        <div className="invoice-panel-head">
          <div>
            <h3>Invoice Records</h3>
            <p>
              Showing {filtered.length} invoice
              {filtered.length !== 1 ? "s" : ""}.
            </p>
          </div>
        </div>

        {pageSlice.length ? (
          <div className="invoice-cards">
            {pageSlice.map((inv) => (
              <article className="invoice-card" key={inv.id}>
                <div className="invoice-card-top">
                  <div>
                    <h3 title={inv.customer || "—"}>{inv.customer || "—"}</h3>

                    <span className="invoice-doc-number">
                      #{inv.invoiceNumber || "—"}
                    </span>
                  </div>

                  <span className={badgeClass(inv.status)}>
                    {inv.status || "Unknown"}
                  </span>
                </div>

                <div className="invoice-card-body">
                  <div className="invoice-meta-grid">
                    <div className="invoice-meta-box">
                      <span>Date</span>
                      <strong>{inv.date || "—"}</strong>
                    </div>

                    <div className="invoice-meta-box">
                      <span>Due Date</span>
                      <strong>{inv.expiryDate || "—"}</strong>
                    </div>

                    <div className="invoice-meta-box total">
                      <span>Total Amount</span>
                      <strong>₦{(inv.total || 0).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                <div className="invoice-card-actions">
                  <button
                    className="invoice-icon-btn"
                    onClick={() => handleEdit(inv.id)}
                    title="Edit invoice"
                  >
                    <Pencil size={16} />
                    Edit
                  </button>

                  <button
                    className="invoice-icon-btn danger"
                    onClick={() => handleDelete(inv.id)}
                    title="Delete invoice"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>

                  {(inv.status || "").toLowerCase() !== "paid" && (
                    <button
                      className="invoice-icon-btn success"
                      onClick={() => handleReceivePayment(inv.id)}
                      title="Receive payment"
                    >
                      <CheckCircle2 size={16} />
                      Receive Payment
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="invoice-empty">
            <div className="invoice-empty-icon">🧾</div>

            <h3>No invoices found</h3>

            <p>
              Create your first invoice to begin tracking sales, receivables and
              customer payments.
            </p>

            <button className="invoice-btn primary" onClick={handleAdd}>
              <Plus size={16} />
              Create Invoice
            </button>
          </div>
        )}

        {/* PAGINATION */}
        {filtered.length > pageSize && (
          <div className="invoice-pagination">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`invoice-page-btn ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
